/* ============================================================
   VELOFLOW homepage — original interaction layer (hand-authored)
   Depends on: Swiper (MIT) exposed as window.Swiper
   ============================================================ */
(function () {
  'use strict';

  var REDUCED = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  var EASE = 'cubic-bezier(.22,.61,.36,1)';

  /* ---------- 1. Scroll reveal (IntersectionObserver, fire once) ---------- */
  function initReveal(root) {
    var nodes = (root || document).querySelectorAll('[data-vf2-reveal]:not([data-vf2-bound])');
    if (!nodes.length) return;

    if (REDUCED || !('IntersectionObserver' in window)) {
      nodes.forEach(function (el) {
        el.setAttribute('data-vf2-bound', '1');
        el.classList.add('is-revealed');
      });
      return;
    }

    var io = new IntersectionObserver(
      function (entries) {
        // group entries appearing in the same tick so stagger is coherent
        var visible = entries.filter(function (e) {
          return e.isIntersecting;
        });
        visible.forEach(function (entry) {
          var el = entry.target;
          var step = parseInt(el.getAttribute('data-vf2-stagger') || '', 10);
          var idx = parseInt(el.getAttribute('data-vf2-index') || '', 10);
          var delay = 0;
          if (!isNaN(step) && !isNaN(idx)) delay = Math.min(step * idx, 640);
          var dur = el.getAttribute('data-vf2-duration') || '700';
          el.style.transitionDuration = dur + 'ms';
          el.style.transitionTimingFunction = EASE;
          el.style.transitionDelay = delay + 'ms';
          el.classList.add('is-revealed');
          io.unobserve(el);
        });
      },
      { threshold: 0.12, rootMargin: '0px 0px -8% 0px' }
    );

    nodes.forEach(function (el) {
      el.setAttribute('data-vf2-bound', '1');
      io.observe(el);
    });
  }

  /* ---------- 2. Sticky header compact state ---------- */
  function initHeader() {
    var header = document.querySelector('[data-vf2-header]');
    if (!header) return;
    var ticking = false;
    var COMPACT_AT = 100;

    function apply() {
      var y = window.pageYOffset || document.documentElement.scrollTop;
      header.classList.toggle('is-compact', y > COMPACT_AT);
      ticking = false;
    }
    function onScroll() {
      if (ticking) return;
      ticking = true;
      window.requestAnimationFrame(apply);
    }
    window.addEventListener('scroll', onScroll, { passive: true });
    apply();
  }

  /* ---------- 3. Mobile drawer ---------- */
  function initDrawer() {
    var drawer = document.querySelector('[data-vf2-drawer]');
    if (!drawer) return;
    var openers = document.querySelectorAll('[data-vf2-drawer-open]');
    var closers = drawer.querySelectorAll('[data-vf2-drawer-close]');

    function setOpen(state) {
      drawer.classList.toggle('is-open', state);
      drawer.setAttribute('aria-hidden', state ? 'false' : 'true');
      document.documentElement.style.overflow = state ? 'hidden' : '';
      if (state) {
        var first = drawer.querySelector('a, button');
        if (first) first.focus({ preventScroll: true });
      }
    }

    openers.forEach(function (b) {
      b.addEventListener('click', function () {
        setOpen(true);
      });
    });
    closers.forEach(function (b) {
      b.addEventListener('click', function () {
        setOpen(false);
      });
    });
    document.addEventListener('keydown', function (e) {
      if (e.key === 'Escape' && drawer.classList.contains('is-open')) setOpen(false);
    });
  }

  /* ---------- 4. Number count-up ---------- */
  function initCounters() {
    var nodes = document.querySelectorAll('[data-vf2-count]');
    if (!nodes.length) return;

    function run(el) {
      var target = parseFloat(el.getAttribute('data-vf2-count'));
      if (isNaN(target)) return;
      var decimals = (el.getAttribute('data-vf2-decimals') || '0') | 0;
      if (REDUCED) {
        el.textContent = target.toFixed(decimals);
        return;
      }
      var dur = parseInt(el.getAttribute('data-vf2-count-duration') || '1500', 10);
      var start = null;
      function frame(ts) {
        if (start === null) start = ts;
        var p = Math.min((ts - start) / dur, 1);
        // easeOutCubic
        var eased = 1 - Math.pow(1 - p, 3);
        el.textContent = (target * eased).toFixed(decimals);
        if (p < 1) window.requestAnimationFrame(frame);
        else el.textContent = target.toFixed(decimals);
      }
      window.requestAnimationFrame(frame);
    }

    if (!('IntersectionObserver' in window)) {
      nodes.forEach(run);
      return;
    }
    var io = new IntersectionObserver(
      function (entries) {
        entries.forEach(function (e) {
          if (!e.isIntersecting) return;
          run(e.target);
          io.unobserve(e.target);
        });
      },
      { threshold: 0.4 }
    );
    nodes.forEach(function (el) {
      // reserve width so the count-up cannot shift layout (CLS guard)
      var target = parseFloat(el.getAttribute('data-vf2-count'));
      var decimals = (el.getAttribute('data-vf2-decimals') || '0') | 0;
      if (!isNaN(target)) {
        el.style.display = 'inline-block';
        el.style.minWidth = String(target.toFixed(decimals)).length + 'ch';
        el.textContent = (0).toFixed(decimals);
      }
      io.observe(el);
    });
  }

  /* ---------- 5. Scene parallax (transform only, rAF-throttled) ---------- */
  function initParallax() {
    if (REDUCED) return;
    var scenes = document.querySelectorAll('[data-vf2-parallax]');
    if (!scenes.length) return;
    if (window.matchMedia('(max-width: 760px)').matches) return;

    var ticking = false;
    function apply() {
      var vh = window.innerHeight;
      scenes.forEach(function (scene) {
        var bg = scene.querySelector('.vf2-scene__bg');
        if (!bg) return;
        var r = scene.getBoundingClientRect();
        if (r.bottom < -200 || r.top > vh + 200) return;
        var progress = (r.top + r.height / 2 - vh / 2) / vh; // ~ -1..1
        var shift = Math.max(-1, Math.min(1, progress)) * -42;
        bg.style.transform = 'translate3d(0,' + shift.toFixed(1) + 'px,0)';
      });
      ticking = false;
    }
    function onScroll() {
      if (ticking) return;
      ticking = true;
      window.requestAnimationFrame(apply);
    }
    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', onScroll, { passive: true });
    apply();
  }

  /* ---------- 6. Swiper: hero + product carousel ---------- */
  function whenSwiper(cb, tries) {
    tries = tries === undefined ? 60 : tries;
    if (window.Swiper) {
      cb();
      return;
    }
    if (tries <= 0) return;
    window.setTimeout(function () {
      whenSwiper(cb, tries - 1);
    }, 100);
  }

  function initHero() {
    var el = document.querySelector('[data-vf2-hero]');
    if (!el) return;
    var node = el.querySelector('.swiper');
    if (!node) return;
    var count = node.querySelectorAll('.swiper-slide').length;

    whenSwiper(function () {
      /* eslint-disable no-new */
      new window.Swiper(node, {
        loop: count > 1,
        speed: 800,
        effect: 'slide',
        autoplay:
          REDUCED || count < 2
            ? false
            : { delay: 5000, disableOnInteraction: false, pauseOnMouseEnter: true },
        pagination: {
          el: el.querySelector('[data-vf2-hero-pagination]'),
          clickable: true
        },
        navigation: {
          prevEl: el.querySelector('[data-vf2-hero-prev]'),
          nextEl: el.querySelector('[data-vf2-hero-next]')
        },
        a11y: { enabled: true },
        watchSlidesProgress: true
      });
    });
  }

  function initProductCarousels() {
    var wraps = document.querySelectorAll('[data-vf2-pcarousel]');
    if (!wraps.length) return;
    whenSwiper(function () {
      wraps.forEach(function (wrap) {
        var node = wrap.querySelector('.swiper');
        if (!node) return;
        new window.Swiper(node, {
          slidesPerView: 1.2,
          spaceBetween: 16,
          speed: 650,
          loop: false,
          watchOverflow: true,
          autoplay: REDUCED
            ? false
            : { delay: 4200, disableOnInteraction: true, pauseOnMouseEnter: true },
          navigation: {
            prevEl: wrap.querySelector('[data-vf2-pc-prev]'),
            nextEl: wrap.querySelector('[data-vf2-pc-next]')
          },
          a11y: { enabled: true },
          breakpoints: {
            560: { slidesPerView: 2, spaceBetween: 20 },
            900: { slidesPerView: 3, spaceBetween: 24 },
            1280: { slidesPerView: 4, spaceBetween: 24 }
          }
        });
      });
    });
  }

  /* ---------- 7. Newsletter inline feedback ---------- */
  function initNewsletter() {
    var forms = document.querySelectorAll('[data-vf2-newsletter]');
    forms.forEach(function (f) {
      f.addEventListener('submit', function () {
        var btn = f.querySelector('button[type="submit"]');
        if (btn) {
          btn.disabled = true;
          btn.textContent = 'Sending…';
        }
      });
    });
  }

  /* ---------- boot ---------- */
  function boot() {
    initHeader();
    initDrawer();
    initReveal(document);
    initCounters();
    initParallax();
    initHero();
    initProductCarousels();
    initNewsletter();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }

  // Shopify theme-editor re-render support
  document.addEventListener('shopify:section:load', function () {
    boot();
  });

  window.VF2 = { initReveal: initReveal };
})();
